import { useCallback, useEffect, useRef, useState } from "react";

// Correct cache headers on index.html only help a user who navigates to the
// site again. A user who leaves a tab open across a deploy keeps running the
// old bundle until something tells them to reload, and sessions here run long.
// This module polls the version.json that the build emits (see vite.config.ts)
// and reports the deployed build once it stops matching the running one.

// Backstop for a tab nobody is touching. Moving around the app is the signal
// that actually matters, so this does not have to be aggressive.
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

// Floor on how often we will hit the server, so that neither a burst of
// navigation nor a user flipping between tabs turns into a request each time.
const MIN_CHECK_GAP_MS = 60 * 1000;

const AUTO_RELOAD_KEY = "spa-auto-reloaded-for";

export interface DeployedVersion {
	version: string;
	gitVersion: string;
	gitCount: string;
	branchName: string;
	commitHash: string;
	shortHash: string;
	commitDate: string;
	buildDate: string;
}

// BASE_URL follows the configured base, which is written without a trailing
// slash ("/spa"), so it cannot be concatenated onto a file name as is.
export function appBaseUrl() {
	const base = import.meta.env.BASE_URL;
	return base.endsWith("/") ? base : `${base}/`;
}

// How long an answer stays good enough to hand to another caller. The sign in
// screen asks through reloadIfStale() shortly after the notice's own check has
// asked - not at the same moment, because Login only mounts once SiteConfig has
// its config - so sharing one in-flight request is not enough to spare the
// second round trip. Remembering the last answer briefly is.
const RESULT_TTL_MS = 10 * 1000;

let lastResult: DeployedVersion | null = null;
let lastResultAt = 0;
let inFlight: Promise<DeployedVersion | null> | null = null;

/**
 * Fetches the deployed build, and returns it only when it differs from the
 * running one. Returns null when they match or the answer is not trustworthy.
 *
 * Reuses an answer newer than `maxAgeMs`, and callers that overlap share one
 * request, so the two checks the sign in screen makes cost a single round trip.
 */
function fetchNewerVersion(maxAgeMs: number): Promise<DeployedVersion | null> {
	if (lastResultAt > 0 && Date.now() - lastResultAt <= maxAgeMs) {
		return Promise.resolve(lastResult);
	}

	if (!inFlight) {
		inFlight = requestNewerVersion().then((version) => {
			lastResult = version;
			lastResultAt = Date.now();
			return version;
		});
		// requestNewerVersion never rejects, so this only clears the slot.
		void inFlight.finally(() => {
			inFlight = null;
		});
	}
	return inFlight;
}

let hasWarned = false;

/**
 * Says once, out loud, that the update check is not getting an answer.
 *
 * Without this the whole feature fails silently: an unreadable version.json
 * looks exactly like "you are up to date", so a deployment problem shows up as
 * users quietly never being offered a new build. Once per page load, because
 * the check runs on every route change.
 */
function warnCheckFailed(reason: string) {
	if (hasWarned) return;
	hasWarned = true;
	console.warn(
		`Update check got no usable answer from ${appBaseUrl()}version.json: ${reason}. ` +
			`Until that is fixed a new deployment cannot be noticed, so neither the ` +
			`reload notice nor the refresh on the sign in screen will happen.`
	);
}

async function requestNewerVersion(): Promise<DeployedVersion | null> {
	// The dev server does not emit version.json, and there is no deployment to
	// fall behind when running against it.
	if (import.meta.env.DEV) return null;

	try {
		// "no-store" plus the timestamp keep the browser and any proxy in
		// between from answering with the copy of version.json that came down
		// with the build we are already running.
		const response = await fetch(`${appBaseUrl()}version.json?t=${Date.now()}`, {
			cache: "no-store",
		});
		if (!response.ok) {
			// A 404 here does not have to mean the file is absent: IIS answers 404
			// for a static file whose extension it has no MIME map for.
			warnCheckFailed(`the server responded ${response.status}`);
			return null;
		}

		// A miss falls through to the SPA's index.html rewrite, which is a 200
		// that is not the file we asked for.
		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("json")) {
			warnCheckFailed(`the response was "${contentType}" rather than JSON`);
			return null;
		}

		const version = (await response.json()) as DeployedVersion;
		if (!version?.commitHash) {
			warnCheckFailed("the response carried no commitHash");
			return null;
		}

		return version.commitHash === __COMMIT_HASH__ ? null : version;
	} catch (error) {
		// Offline, or a deploy is mid flight and the file is briefly missing or
		// truncated. Worth saying once, but it is expected to come back on its
		// own, so the next check just tries again.
		warnCheckFailed(String(error));
		return null;
	}
}

/**
 * Reloads the page when a newer build has been deployed, taking the update
 * without asking. Only call this where there is nothing to lose, such as the
 * sign in screen: no one is signed in there, so nothing is interrupted.
 *
 * Ignores the rate limit, because the answer has to be current to act on it.
 *
 * Reloads at most once per deployed build. If a reload does not actually
 * produce the new bundle - a caching problem, say - the guard keeps that from
 * becoming a reload loop, and the notice is left to handle it instead.
 */
export async function reloadIfStale() {
	const version = await fetchNewerVersion(RESULT_TTL_MS);
	if (!version) return false;

	try {
		if (sessionStorage.getItem(AUTO_RELOAD_KEY) === version.commitHash) return false;
		sessionStorage.setItem(AUTO_RELOAD_KEY, version.commitHash);
	} catch {
		// Storage is unavailable, so we cannot promise this happens only once.
		// Leave it to the notice rather than risk reloading repeatedly.
		return false;
	}

	// reload() rather than a fresh navigation: history.state survives it, so
	// the router's "from" location is still there to send the user back to
	// where they were headed once they sign in.
	window.location.reload();
	return true;
}

/**
 * Returns the deployed build once it differs from the running one, or null.
 *
 * `activityKey` is any value that changes when the user does something, such
 * as the current pathname. Each new value is an opportunity to check.
 */
export default function useVersionCheck(activityKey: string) {
	const [deployed, setDeployed] = useState<DeployedVersion | null>(null);
	const lastCheckedRef = useRef(0);
	const foundRef = useRef(false);
	const unmountedRef = useRef(false);

	const check = useCallback(async () => {
		if (unmountedRef.current || foundRef.current) return;

		const now = Date.now();
		if (now - lastCheckedRef.current < MIN_CHECK_GAP_MS) return;
		lastCheckedRef.current = now;

		const version = await fetchNewerVersion(RESULT_TTL_MS);
		if (!version || unmountedRef.current) return;

		foundRef.current = true;
		setDeployed(version);
	}, []);

	// Someone moving around the app is someone who may have been sitting on a
	// tab since before the deploy, so every route change is a chance to look.
	// This also covers the first render.
	useEffect(() => {
		check();
	}, [activityKey, check]);

	useEffect(() => {
		unmountedRef.current = false;

		function checkWhenVisible() {
			if (document.visibilityState === "visible") check();
		}

		const timer = window.setInterval(checkWhenVisible, CHECK_INTERVAL_MS);
		document.addEventListener("visibilitychange", checkWhenVisible);

		return () => {
			unmountedRef.current = true;
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", checkWhenVisible);
		};
	}, [check]);

	return deployed;
}
